import { css, numberInputTokens as tokens } from '@salutejs/plasma-new-hope/css';
import {
    bodyS,
    bodySBold,
    inverseSurfaceTransparentSecondary,
    inverseTextPrimary,
    inverseTextPrimaryActive,
    inverseTextPrimaryHover,
    inverseTextTertiary,
    onDarkSurfaceTransparentSecondary,
    onDarkTextPrimary,
    onDarkTextPrimaryActive,
    onDarkTextPrimaryHover,
    onDarkTextTertiary,
    surfaceAccent,
    surfaceAccentActive,
    surfaceAccentHover,
    surfaceSolidDefault,
    surfaceSolidDefaultActive,
    surfaceSolidDefaultHover,
    surfaceTransparentNegative,
    surfaceTransparentPrimary,
    surfaceTransparentSecondary,
    surfaceTransparentSecondaryActive,
    surfaceTransparentSecondaryHover,
    textAccent,
    textNegative,
    textPrimary,
    textPrimaryActive,
    textPrimaryHover,
    textTertiary,
} from '@salutejs/sdds-themes/tokens/sdds_finai';

export const config = {
    defaults: {
        view: 'default',
        size: 's',
        shape: 'cornered',
        inputBackgroundType: 'fill',
    },
    variations: {
        view: {
            default: css`
                ${tokens.caretColor}: ${textAccent};
                ${tokens.errorColor}: ${textNegative};
                ${tokens.backgroundErrorColor}: ${surfaceTransparentNegative};

                ${tokens.color}: ${textPrimary};
                ${tokens.colorSolid}: ${inverseTextPrimary};
                ${tokens.additionalTextColor}: ${textTertiary};
                ${tokens.additionalTextColorSolid}: ${inverseTextTertiary};
                ${tokens.backgroundColorFocus}: ${surfaceTransparentSecondary};
                ${tokens.backgroundColorSolidFocus}: ${inverseSurfaceTransparentSecondary};
                ${tokens.backgroundColorSolid}: ${surfaceSolidDefault};

                ${tokens.iconButtonColor}: ${inverseTextPrimary};
                ${tokens.iconButtonBackgroundColor}: ${surfaceSolidDefault};
                ${tokens.iconButtonColorHover}: ${inverseTextPrimary};
                ${tokens.iconButtonColorSolidHover}: ${inverseTextPrimaryHover};
                ${tokens.iconButtonBackgroundColorHover}: ${surfaceSolidDefaultHover};
                ${tokens.iconButtonColorActive}: ${inverseTextPrimary};
                ${tokens.iconButtonColorSolidActive}: ${inverseTextPrimaryActive};
                ${tokens.iconButtonBackgroundColorActive}: ${surfaceSolidDefaultActive};

                ${tokens.actionButtonDisabledOpacity}: 0.4;

                ${tokens.loaderSpinnerColor}: inherit;
                ${tokens.loaderSpinnerColorSolid}: inherit;
            `,
            secondary: css`
                ${tokens.caretColor}: ${textAccent};
                ${tokens.errorColor}: ${textNegative};
                ${tokens.backgroundErrorColor}: ${surfaceTransparentNegative};

                ${tokens.color}: ${textPrimary};
                ${tokens.colorSolid}: ${textPrimary};
                ${tokens.additionalTextColor}: ${textTertiary};
                ${tokens.additionalTextColorSolid}: ${textTertiary};
                ${tokens.backgroundColorFocus}: ${surfaceTransparentSecondary};
                ${tokens.backgroundColorSolidFocus}: ${surfaceTransparentSecondary};
                ${tokens.backgroundColorSolid}: ${surfaceTransparentSecondary};

                ${tokens.iconButtonColor}: ${textPrimary};
                ${tokens.iconButtonBackgroundColor}: ${surfaceTransparentSecondary};
                ${tokens.iconButtonColorHover}: ${textPrimary};
                ${tokens.iconButtonColorSolidHover}: ${textPrimaryHover};
                ${tokens.iconButtonBackgroundColorHover}: ${surfaceTransparentSecondaryHover};
                ${tokens.iconButtonColorActive}: ${textPrimary};
                ${tokens.iconButtonColorSolidActive}: ${textPrimaryActive};
                ${tokens.iconButtonBackgroundColorActive}: ${surfaceTransparentSecondaryActive};

                ${tokens.actionButtonDisabledOpacity}: 0.4;

                ${tokens.loaderSpinnerColor}: inherit;
                ${tokens.loaderSpinnerColorSolid}: inherit;
            `,
            accent: css`
                ${tokens.caretColor}: ${textAccent};
                ${tokens.caretColorSolid}: ${inverseTextPrimary};
                ${tokens.errorColor}: ${textNegative};
                ${tokens.backgroundErrorColor}: ${surfaceTransparentNegative};

                ${tokens.color}: ${textPrimary};
                ${tokens.colorSolid}: ${onDarkTextPrimary};
                ${tokens.additionalTextColor}: ${textTertiary};
                ${tokens.additionalTextColorSolid}: ${onDarkTextTertiary};
                ${tokens.backgroundColorFocus}: ${surfaceTransparentSecondary};
                ${tokens.backgroundColorSolidFocus}: ${onDarkSurfaceTransparentSecondary};
                ${tokens.backgroundColorSolid}: ${surfaceAccent};

                ${tokens.iconButtonColor}: ${onDarkTextPrimary};
                ${tokens.iconButtonBackgroundColor}: ${surfaceAccent};
                ${tokens.iconButtonColorHover}: ${inverseTextPrimary};
                ${tokens.iconButtonColorSolidHover}: ${onDarkTextPrimaryHover};
                ${tokens.iconButtonBackgroundColorHover}: ${surfaceAccentHover};
                ${tokens.iconButtonColorActive}: ${inverseTextPrimary};
                ${tokens.iconButtonColorSolidActive}: ${onDarkTextPrimaryActive};
                ${tokens.iconButtonBackgroundColorActive}: ${surfaceAccentActive};

                ${tokens.actionButtonDisabledOpacity}: 0.4;

                ${tokens.loaderSpinnerColor}: inherit;
                ${tokens.loaderSpinnerColorSolid}: inherit;
            `,
            clear: css`
                ${tokens.caretColor}: ${textAccent};
                ${tokens.caretColorSolid}: ${textAccent};
                ${tokens.errorColor}: ${textNegative};
                ${tokens.backgroundErrorColor}: ${surfaceTransparentNegative};

                ${tokens.color}: ${textPrimary};
                ${tokens.colorSolid}: ${textPrimary};
                ${tokens.iconButtonColorSolidHover}: ${textPrimaryHover};
                ${tokens.additionalTextColor}: ${textTertiary};
                ${tokens.additionalTextColorSolid}: ${textTertiary};
                ${tokens.backgroundColorFocus}: ${surfaceTransparentSecondary};
                ${tokens.backgroundColorSolidFocus}: ${surfaceTransparentSecondary};
                ${tokens.backgroundColorSolid}: transparent;

                ${tokens.iconButtonColor}: ${textPrimary};
                ${tokens.iconButtonBackgroundColor}: transparent;
                ${tokens.iconButtonColorHover}: ${textPrimaryHover};
                ${tokens.iconButtonBackgroundColorHover}: transparent;
                ${tokens.iconButtonColorActive}: ${textPrimaryActive};
                ${tokens.iconButtonBackgroundColorActive}: transparent;

                ${tokens.actionButtonDisabledOpacity}: 0.4;

                ${tokens.loaderSpinnerColor}: inherit;
                ${tokens.loaderSpinnerColorSolid}: inherit;
            `,
        },
        size: {
            s: css`
                ${tokens.rootMinWidth}: 8.75rem;
                ${tokens.iconButtonHeight}: 2.5rem;
                ${tokens.iconButtonWidth}: 2.5rem;
                ${tokens.iconButtonPadding}: 1rem;
                ${tokens.iconButtonFontFamily}: ${bodyS.fontFamily};
                ${tokens.iconButtonFontSize}: ${bodyS.fontSize};
                ${tokens.iconButtonFontStyle}: ${bodyS.fontStyle};
                ${tokens.iconButtonFontWeight}: ${bodySBold.fontWeight};
                ${tokens.iconButtonLetterSpacing}: ${bodyS.letterSpacing};
                ${tokens.iconButtonLineHeight}: ${bodyS.lineHeight};

                ${tokens.iconButtonRadius}: 0.625rem;
                ${tokens.iconButtonSegmentationRadius}: 0.25rem;

                ${tokens.inputWrapperMargin}: 0 0.125rem;
                ${tokens.inputWrapperBorderRadius}: 0.625rem;
                ${tokens.textWrapperHeight}: 2.5rem;
                ${tokens.textWrapperPadding}: 0.688rem 0.875rem;

                ${tokens.fontFamily}: ${bodyS.fontFamily};
                ${tokens.fontSize}: ${bodyS.fontSize};
                ${tokens.fontStyle}: ${bodyS.fontStyle};
                ${tokens.fontWeight}: ${bodyS.fontWeight};
                ${tokens.letterSpacing}: ${bodyS.letterSpacing};
                ${tokens.lineHeight}: ${bodyS.lineHeight};

                ${tokens.textBeforeMarginRight}: 0.25rem;
                ${tokens.textAfterMarginLeft}: 0.25rem;

                ${tokens.loaderSpinnerSize}: 1.375rem;
            `,
        },
        shape: {
            cornered: css``,
            pilled: css`
                ${tokens.iconButtonRadius}: 50%;
            `,
        },
        inputBackgroundType: {
            fill: css`
                ${tokens.backgroundColor}: ${surfaceTransparentPrimary};
                ${tokens.backgroundColorHover}: ${surfaceTransparentPrimary};
            `,
            clear: css`
                ${tokens.backgroundColor}: transparent;
                ${tokens.backgroundColorHover}: transparent;
            `,
        },
        segmentation: {
            default: css``,
            segmented: css``,
            solid: css`
                ${tokens.iconButtonSegmentationRadius}: 0;
            `,
        },
        disabled: {
            true: css`
                ${tokens.disabledOpacity}: 0.4;
            `,
        },
    },
};
